// Model picker for role and agent forms: a text field you can type any model
// into, with the models the chosen connection can use as suggestions (fetched
// from its provider with its login, or the models Claude Code accepts when
// its login can't list them). A role only knows an executor kind: then the
// first connection of that kind is asked.
// Suggestions filter as you type; arrow keys, Enter and Escape work as in a
// normal combobox. Empty means "the executor's default".

import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { connectionsApi, type ExecutorModelList } from '../lib/api-client'

type Model = ExecutorModelList['models'][number]

/** One row in the suggestion list: the "default" choice or a model. */
type Row = { kind: 'default' } | { kind: 'model'; model: Model }

export function ModelPicker({
  id,
  workspaceId,
  target,
  value,
  onChange,
  emptyLabel = 'The executor’s default',
}: {
  id: string
  workspaceId: string
  /** Whose models to suggest; null when nothing is chosen yet. */
  target: { connectionId: string } | { kind: string } | null
  value: string
  onChange: (model: string) => void
  /** What an empty field means here. */
  emptyLabel?: string
}) {
  const queryClient = useQueryClient()
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [openState, setOpenState] = useState(false)
  /** Open upwards when there's no room below (e.g. at the bottom of a drawer). */
  const [above, setAbove] = useState(false)
  const open = openState
  function setOpen(next: boolean | ((o: boolean) => boolean)): void {
    setOpenState((current) => {
      const value = typeof next === 'function' ? next(current) : next
      if (value && !current) {
        const rect = inputRef.current?.getBoundingClientRect()
        // Room for the list (max-h-72 = 288px) plus a sticky footer below.
        if (rect)
          setAbove(window.innerHeight - rect.bottom < 360 && rect.top > 320)
      }
      return value
    })
  }
  const [active, setActive] = useState(-1)
  /** Filter only while typing; opening with the arrow shows everything. */
  const [query, setQuery] = useState<string | null>(null)

  const targetKey = target
    ? 'connectionId' in target
      ? `id:${target.connectionId}`
      : `kind:${target.kind}`
    : ''
  const executorType = targetKey
  const modelsQuery = useQuery({
    queryKey: ['executor-models', targetKey, workspaceId],
    queryFn: () => connectionsApi.models(target!, workspaceId),
    enabled: workspaceId !== '' && target != null,
    staleTime: 5 * 60 * 1000,
  })
  const list = modelsQuery.data
  const models = useMemo(
    () => (list && list.source !== 'unavailable' ? list.models : []),
    [list]
  )
  const hasSuggestions = executorType !== '' && models.length > 0

  const rows: Row[] = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase()
    const matching = q
      ? models.filter(
          (m) =>
            m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
        )
      : models
    return [
      ...(q ? [] : [{ kind: 'default' } as Row]),
      ...matching.map((model) => ({ kind: 'model', model }) as Row),
    ]
  }, [models, query])

  async function refresh(): Promise<void> {
    if (!target) return
    const fresh = await connectionsApi.models(target, workspaceId, true)
    queryClient.setQueryData(['executor-models', targetKey, workspaceId], fresh)
  }

  function choose(row: Row): void {
    onChange(row.kind === 'default' ? '' : row.model.id)
    setOpen(false)
    setQuery(null)
    setActive(-1)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!hasSuggestions) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        setActive(0)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (i + step + rows.length) % rows.length)
    } else if (event.key === 'Enter' && open) {
      // Enter picks the highlighted suggestion; otherwise the typed text stays.
      event.preventDefault()
      const row = rows[active]
      if (row) choose(row)
      else setOpen(false)
    } else if (event.key === 'Escape' && open) {
      event.preventDefault()
      setOpen(false)
      setQuery(null)
    }
  }

  const selected = models.find((m) => m.id === value)
  const activeId = open && active >= 0 ? `${listId}-${active}` : undefined

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          className={`field-input font-mono placeholder:font-sans ${hasSuggestions ? 'pr-9' : ''}`}
          value={value}
          placeholder={emptyLabel}
          spellCheck={false}
          autoCapitalize="none"
          autoComplete="off"
          role={hasSuggestions ? 'combobox' : undefined}
          aria-expanded={hasSuggestions ? open : undefined}
          aria-controls={hasSuggestions ? listId : undefined}
          aria-autocomplete={hasSuggestions ? 'list' : undefined}
          aria-activedescendant={activeId}
          onChange={(event) => {
            onChange(event.target.value)
            setQuery(event.target.value)
            setActive(-1)
            if (hasSuggestions) setOpen(true)
          }}
          onFocus={() => {
            if (hasSuggestions) setOpen(true)
          }}
          onBlur={() => {
            setOpen(false)
            setQuery(null)
          }}
          onKeyDown={onKeyDown}
        />
        {hasSuggestions ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Show models"
            className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-ink-3 hover:text-ink"
            onMouseDown={(event) => {
              // Keep focus in the field; just open or close the list.
              event.preventDefault()
              inputRef.current?.focus()
              setQuery(null)
              setOpen((o) => !o)
            }}
          >
            <svg
              viewBox="0 0 20 20"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
            >
              <path
                d="m5 8 5 5 5-5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        {open && hasSuggestions ? (
          <ul
            id={listId}
            role="listbox"
            className={`absolute z-20 max-h-72 w-full overflow-auto rounded-control border border-line bg-surface py-1 shadow-overlay ${
              above ? 'bottom-full mb-1' : 'mt-1'
            }`}
          >
            {rows.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink-3">
                No suggestion matches. The typed model is used as it is.
              </li>
            ) : null}
            {rows.map((row, index) => {
              const previous = rows[index - 1]
              const group = row.kind === 'model' ? (row.model.group ?? '') : ''
              const previousGroup =
                previous?.kind === 'model' ? (previous.model.group ?? '') : null
              const showGroup =
                row.kind === 'model' && group !== '' && group !== previousGroup
              const isSelected =
                row.kind === 'default' ? value === '' : row.model.id === value
              return (
                <li
                  key={row.kind === 'default' ? '__default' : row.model.id}
                  role="presentation"
                >
                  {showGroup ? (
                    <div className="px-3 pb-1 pt-2 text-xs font-medium text-ink-3">
                      {group}
                    </div>
                  ) : null}
                  <div
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      choose(row)
                    }}
                    onMouseEnter={() => setActive(index)}
                    className={`flex cursor-pointer items-baseline justify-between gap-3 px-3 py-1.5 ${
                      index === active ? 'bg-sunken' : ''
                    }`}
                  >
                    {row.kind === 'default' ? (
                      <span className="text-base text-ink-2">{emptyLabel}</span>
                    ) : (
                      <>
                        <span className="min-w-0 truncate text-base text-ink">
                          {row.model.name}
                        </span>
                        {row.model.name !== row.model.id ? (
                          <span className="shrink-0 font-mono text-xs text-ink-3">
                            {row.model.id}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>

      <p className="mt-1.5 text-sm text-ink-3">
        {!executorType ? (
          'Choose a connection to get suggestions.'
        ) : modelsQuery.isLoading ? (
          'Loading models…'
        ) : list?.source === 'unavailable' ? (
          <>
            {list.message}{' '}
            {list.message?.includes('Executors page') ? (
              <Link to="/executors" className="link">
                Executors
              </Link>
            ) : null}{' '}
            <button
              type="button"
              className="link"
              onClick={() => void refresh()}
            >
              Try again
            </button>
          </>
        ) : list?.source === 'builtin' ? (
          list.message
        ) : (
          <>
            {selected ? `${selected.name}. ` : null}
            {models.length} models from the provider, or type another.{' '}
            <button
              type="button"
              className="link"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
          </>
        )}
      </p>
    </div>
  )
}
